import classNames from 'classnames';

import Modal from '../../../components/Modal';
import ECharts from '../../../components/ECharts';
import Radio from '../../../components/Radio';
import TopCompanyLabel from '../../../components/TopCompanyLabel';
import {
  financialInvestmentLegends,
  financialPeriods,
} from '../../../hooks/useInvestmentModal';
import { getMinMax, toPlainString } from '../../../utils/helpers';
import { formatDisplayNumber } from '../../../utils/format';

export const getFinancialData = (financialData, selectedFields) => {
  const xAxisData = [];
  const tmpProfitData = [];
  const seriesData = [];
  for (let i = 0; i < financialData?.length; i++) {
    const financial = financialData[i];
    xAxisData.push(financial.thoigian);
  }
  for (let j = 0; j < selectedFields.length; j++) {
    const field = selectedFields[j];
    const currentField = financialInvestmentLegends.find(
      (f) => f.value === field
    );
    const data = [];
    for (let i = 0; i < financialData?.length; i++) {
      const financial = financialData[i];
      data.push({
        value: parseFloat(financial[field]?.toFixed(2)) || 0,
        label: {
          show: true,
          rotate: 90,
          align: financial[field] < 0 ? 'right' : 'left',
          verticalAlign: 'middle',
          position: financial[field] < 0 ? 'insideTop' : 'insideBottom',
          distance: 10,
          formatter: function (param) {
            return formatDisplayNumber(param.value) + ' tỷ';
          },
        },
      });
      tmpProfitData.push(financial[field]);
    }
    const item = financialInvestmentLegends.find((l) => l.value === field);
    seriesData.push({
      name: currentField?.label,
      type: 'bar',
      emphasis: {
        focus: 'series',
      },
      data,
      barMaxWidth: 40,
      showBackground: true,
      backgroundStyle: {
        color: 'rgba(180, 180, 180, 0.2)',
      },
      itemStyle: {
        color: item?.color,
      },
    });
  }

  return {
    xAxisData,
    tmpProfitData,
    seriesData,
  };
};

export default function FinancialChartModal({
  isOpen,
  onCloseModal,
  isLoading,
  chartType,
  financialField = [],
  financialChartType,
  setFinancialChartType,
  handleLegendChange,
  financialData,
  isDarkMode,
  selectedStock,
}) {
  const getTitle = () => {
    switch (chartType) {
      case 'doanhthuthuan':
        return 'Doanh thu thuần';
      case 'lnsauthue':
        return 'Lợi nhuận ròng';
      default:
        return 'Tổng nợ vay';
    }
  };

  const getChartOptions = () => {
    const { xAxisData, seriesData, tmpProfitData } = getFinancialData(
      financialData,
      financialField
    );
    const bgColor = isDarkMode ? '#1a1c23' : '#fff';
    const color = isDarkMode ? '#e5e7eb' : '#24262d';
    const minMax = getMinMax(tmpProfitData);
    let min = minMax[0];
    let max = minMax[1];
    if (minMax[0] < 0) {
      if (min < 0 && max > 0) {
        if (min * -1 > max) {
          max = min * -1;
        } else {
          min = max * -1;
        }
      } else if (min < 0 && max < 0) {
        max = min * -1;
      } else if (min > 0) {
        min = max * -1;
      }
    } else {
      min = 0;
    }
    return {
      backgroundColor: bgColor,
      tooltip: {
        trigger: 'axis',
        formatter(params) {
          let html = '';
          for (let i = 0; i < params.length; i++) {
            html += `
              <div class="flex justify-between w-full">
                <div class="mr-4">${params[i].marker} ${
              params[i].seriesName
            }:</div>
                <div class="font-bold">${formatDisplayNumber(
                  params[i].value
                )} tỷ</div>
              </div>
            `;
          }
          return `
            <div>
              <div class="font-bold">${params[0].axisValue}</div>
              <div>${html}</div>
            </div>
          `;
        },
      },
      grid: {
        left: 70,
        right: 10,
        top: 10,
        bottom: 30,
      },
      xAxis: [
        {
          type: 'category',
          axisTick: {
            show: true,
          },
          data: xAxisData,
          axisLabel: {
            color,
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            show: true,
            formatter(value) {
              let newValue = parseFloat(
                Number(toPlainString(value)).toFixed(2)
              );
              return `${formatDisplayNumber(newValue, ',', 0)} tỷ`;
            },
            color,
          },
          splitLine: {
            show: false,
          },
          axisLine: {
            show: true,
          },
          axisTick: {
            show: true,
          },
          min,
          max,
          interval: (max - min) / 6,
        },
      ],
      series: seriesData,
    };
  };

  const getModalTitle = () => {
    return (
      <div className="flex items-center flex-wrap md:flex-nowrap">
        <div className="flex items-center">
          <span>
            {getTitle()} - {selectedStock?.code}
          </span>
          <TopCompanyLabel label={selectedStock?.label} />
        </div>
        <span className="pl-1">- {selectedStock?.name}</span>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onCloseModal={onCloseModal}
      title={getModalTitle()}
      isLoading={isLoading}
      mainContentClassName="modal-center max-w-[96vw] md:max-w-[70vw] m-auto w-full"
      childrenClassName=""
      childrenWrapperClassName=""
    >
      <div className="w-full flex justify-end">
        <Radio
          name="tradingOption"
          options={financialPeriods}
          defaultValue={financialChartType}
          onChange={setFinancialChartType}
        />
      </div>
      <div>
        <ECharts options={{ ...getChartOptions() }} height="350px" notMerge />
      </div>
      <ul className="flex items-center mt-3">
        {financialInvestmentLegends.map((legend) => (
          <li key={legend.value} className="mr-4 flex items-center">
            <button
              type="button"
              onClick={() => handleLegendChange(legend.value)}
              className={classNames(
                financialField.includes(legend.value)
                  ? 'opacity-100'
                  : 'opacity-30'
              )}
            >
              <span
                className="w-3.5 h-3.5 min-w-3.5 rounded-full inline-block mr-1"
                style={{ backgroundColor: legend.color }}
              ></span>
              <span>{legend.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </Modal>
  );
}
